<script setup lang="ts">
import type { ReadonlyDeep } from 'type-fest';
import type { Stop } from '@/components/diagrams/WideJobStopDiagram.vue';
import type { ValidData as ChooseOptionStopDialogFormValidData } from '@/components/dialogForms/ChooseOptionStopDialogForm.vue';
import type { ValidData as DashboardStopDialogFormValidData } from '@/components/dialogForms/DashboardStopDialogForm.vue';
import type { DialogName } from '@/stores/useDialogStore';
import type * as openapi from '@/helpers/api/types/openapiOverrides';

import { computed, ref, useAttrs } from 'vue';

import WideJobStopDiagram from '@/components/diagrams/WideJobStopDiagram.vue';
import { useDialogStore } from '@/stores/useDialogStore';
import * as dashboardTableHelpers from '@/helpers/tables/dashboard';
import * as oneTrackingJobHelpers from '@/helpers/trackingJob/oneTrackingJob';

export interface Props {
  job?: ReadonlyDeep<
    Pick<
      openapi.JobDetailedOpenapi,
      | 'is_pod_required'
      | 'main_stop'
      | 'rockhopper_date'
      | 'rockhopper_id'
      | 'status'
      | 'stops'
    >
  >;
  refreshDetailedJob?: () => Promise<void>;
}
const props = withDefaults(defineProps<Props>(), {
  job: undefined,
  refreshDetailedJob: undefined,
});

const attrs = useAttrs();
const dialogStore = useDialogStore();

const clickStopDriver = ref(NaN);
const clickStopNum = ref(NaN);
const clickIsLastStop = ref(false);
const clickIsForm = ref<boolean | undefined>();
const clickHasArrived = ref<boolean | undefined>();
const clickIsCompleted = ref<boolean | undefined>();
const clickIsFutile = ref<boolean | undefined>();

const chooseOptionStopDialogData = {
  confirm: async (formData: ChooseOptionStopDialogFormValidData) => {
    if (!props.job) return;

    clickIsForm.value = false;
    if (props.job.is_pod_required) {
      if (formData.stopType === 'completed') {
        clickIsForm.value = true;
      }
      if (formData.stopType === 'departed') {
        if (
          oneTrackingJobHelpers.checkMainStop(
            props.job,
            clickStopNum.value as 1 | 2,
          )
        ) {
          clickIsForm.value = true;
        }
      }
    }

    if (formData.stopType === 'arrived') {
      clickHasArrived.value = true;
      clickIsCompleted.value = false;
    }
    if (formData.stopType === 'departed') {
      clickHasArrived.value = false;
      clickIsCompleted.value = false;
    }
    if (formData.stopType === 'completed') {
      clickHasArrived.value = false;
      clickIsCompleted.value = true;
    }
    if (formData.stopType === 'futile') {
      clickIsFutile.value = true;
    }
    if (formData.stopType === 'cancel') {
      clickIsFutile.value = false;
    }
    if (
      formData.stopType === 'arrived' ||
      formData.stopType === 'departed' ||
      formData.stopType === 'completed'
    ) {
      arrivedDepartedStopDialogConfirm.value = dashboardTableHelpers.getConfirm(
        props.job.rockhopper_id,
        props.job.rockhopper_date,
        clickStopNum.value,
        clickStopDriver.value,
        formData.stopType === 'completed',
        formData.stopType === 'departed',
      );
      openArrivedDepartedStopDialog();
    } else if (
      formData.stopType === 'futile' ||
      formData.stopType === 'cancel'
    ) {
      const jobs = [
        {
          jobId: props.job.rockhopper_id,
          jobDate: props.job.rockhopper_date,
          driver: clickStopDriver.value,
        },
      ];
      const dialogName: DialogName = 'cancelCloseStop';
      dialogStore
        .open(dialogName, {
          type: clickIsFutile.value ? 'futile' : 'cancel',
          jobs,
        })
        .confirmed(async function () {
          if (props.refreshDetailedJob) await props.refreshDetailedJob();
        });
    }
  },
};

function openChooseOptionStopDialog() {
  const dialogName: DialogName = 'chooseOptionStop';
  // TODO don't blink with GoToTopBtn
  dialogStore.open(dialogName, {
    ...chooseOptionStopDialogData,
    stopNum: clickIsLastStop.value ? undefined : clickStopNum.value,
  });
}

function openArrivedDepartedStopDialog() {
  const dialogName: DialogName = 'arrivedDepartedStop';
  dialogStore
    .open(dialogName, {
      confirm: arrivedDepartedStopDialogConfirm.value,
      driver: clickStopDriver.value,
      jobId: props.job?.rockhopper_id,
      stopNum: clickStopNum.value,
      stopNames: stopNames.value,
      isForm: clickIsForm.value,
      hasArrived: clickHasArrived.value,
      isCompleted: clickIsCompleted.value,
    })
    .confirmed(async function () {
      if (props.refreshDetailedJob) await props.refreshDetailedJob();
    });
}

const arrivedDepartedStopDialogConfirm =
  ref<(formData?: DashboardStopDialogFormValidData) => Promise<unknown>>();

const stopNames = computed(() => {
  // TODO
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  return props.job?.stops?.map((stop) => stop.name);
});

const diagramStops = computed(() => {
  if (!props.job?.stops) return [];
  const _diagramStops = props.job.stops.map((stop) => {
    return {
      name: stop.name,
      disabled: false,
      isTruckHere: false,
      isTruckBefore: false,
    };
  });
  const jobStatus = props.job.status;
  // TODO oneTrackingJobHelpers.getStatus(job, categoryJobs)
  if (jobStatus === 'Cancelled') {
    _diagramStops.forEach((_diagramStop) => {
      _diagramStop.disabled = true;
      _diagramStop.isTruckHere = false;
      _diagramStop.isTruckBefore = false;
    });
  } else if (jobStatus === 'Completed') {
    _diagramStops.forEach((_diagramStop) => {
      _diagramStop.disabled = true;
      _diagramStop.isTruckHere = false;
      _diagramStop.isTruckBefore = false;
    });
    _diagramStops[_diagramStops.length - 1].isTruckHere = true;
  } else if (jobStatus === 'POD 2 Completed') {
    _diagramStops[0].disabled = true;
    _diagramStops[1].disabled = true;
    _diagramStops[1].isTruckHere = true;
    _diagramStops[1].isTruckBefore = false;
  } else if (jobStatus === 'Arrived at Stop 2') {
    _diagramStops[0].disabled = true;
    _diagramStops[1].isTruckHere = true;
    _diagramStops[1].isTruckBefore = true;
  } else if (jobStatus === 'Departed from Stop 1') {
    _diagramStops[0].disabled = true;
    _diagramStops[0].isTruckHere = true;
    _diagramStops[1].isTruckBefore = false;
  } else if (jobStatus === 'Allocated') {
    _diagramStops[0].isTruckHere = true;
    _diagramStops[0].isTruckBefore = true;
  } else if (jobStatus === 'Unknown') {
    _diagramStops[0].isTruckHere = true;
    _diagramStops[0].isTruckBefore = true;
  }
  return _diagramStops;
});

function handleMarkerClick(stopNum: number, stop: Stop): void {
  if (!props.job?.stops) return;
  clickStopDriver.value = props.job.stops[stopNum - 1].driver_is || NaN;
  clickIsLastStop.value = Boolean(
    stopNum && stopNum >= diagramStops.value.length,
  );
  if (clickIsLastStop.value) {
    // this is the last marker, you can do only finish here
    clickStopNum.value = stopNum;
    openChooseOptionStopDialog();
  } else {
    if (stop.isTruckHere && stop.isTruckBefore) {
      // you can do departed only, it must be disabled otherwise
      clickStopNum.value = stopNum;
      clickHasArrived.value = false;
      clickIsCompleted.value = false;

      clickIsForm.value = false;
      if (props.job.is_pod_required) {
        if (
          oneTrackingJobHelpers.checkMainStop(
            props.job,
            clickStopNum.value as 1 | 2,
          )
        ) {
          clickIsForm.value = true;
        }
      }

      arrivedDepartedStopDialogConfirm.value = dashboardTableHelpers.getConfirm(
        props.job.rockhopper_id,
        props.job.rockhopper_date,
        stopNum,
        clickStopDriver.value,
        false,
        true,
      );
      openArrivedDepartedStopDialog();
    } else {
      // the truck is somewhere before this marker, it must be disabled otherwise
      clickStopNum.value = stopNum;
      openChooseOptionStopDialog();
    }
  }
}
</script>

<template>
  <WideJobStopDiagram
    :stops="diagramStops"
    :class="$style.detailedJobWideJobStopDiagram"
    data-testid="detailedJobWideJobStopDiagram"
    v-bind="attrs"
    @marker-click="handleMarkerClick"
  />
</template>

<!-- prettier-ignore -->
<style module lang="scss">
@use '@/styles/colors';
@use '@/styles/variables';

.detailedJobWideJobStopDiagram {
}
</style>
